<template>
    <div class="container-fluid">
        <div class="card">
            <div class="card-body">
                <div class="left-right-row row">
                    <div class="right">
                        <button
                            type="button"
                            class="btn btn-primary mb-2"
                            @click="openCreateModal"
                        >
                            <i class="fa fa-plus" /> New Vehicle
                        </button>
                    </div>
                </div>
                <p v-if="loading">
                    Loading... <i class="fa fa-spinner fa-spin" />
                </p>
                <p v-if="!loading && vehicles.length === 0">
                    No vehicles yet.
                </p>
                <table
                    v-if="!loading && vehicles.length"
                    class="table"
                >
                    <thead>
                        <tr>
                            <th>Registration</th>
                            <th>Type</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="vehicle in vehicles"
                            :key="vehicle.id"
                        >
                            <td>
                                <button
                                    type="button"
                                    class="btn btn-link p-0"
                                    @click="openEditModal(vehicle)"
                                >
                                    {{ vehicle.registration }}
                                </button>
                            </td>
                            <td>{{ vehicle.type || '-' }}</td>
                            <td class="text-right">
                                <button
                                    type="button"
                                    class="btn btn-sm btn-link row-icon"
                                    @click="openEditModal(vehicle)"
                                >
                                    <i class="fa fa-pencil-alt" />
                                </button>
                                <button
                                    type="button"
                                    class="btn btn-sm btn-link row-icon text-danger"
                                    @click="promptDelete(vehicle)"
                                >
                                    <i class="fa fa-times" />
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <modal
            ref="vehicleModal"
            :title="modalTitle"
            @close="onVehicleModalClose"
        >
            <form
                v-if="editingVehicle"
                action=""
                @submit.prevent="saveVehicle"
            >
                <div class="form-group">
                    <label for="vehicle_registration">Registration</label>
                    <input
                        id="vehicle_registration"
                        type="text"
                        placeholder="e.g. AB12 CDE"
                        v-model="editingVehicle.registration"
                        class="form-control"
                        :class="{ 'is-invalid': errors.registration }"
                    >
                    <small
                        class="form-error"
                        v-if="errors.registration"
                    >{{ errors.registration[0] }}</small>
                </div>
                <div class="form-group">
                    <label for="vehicle_type">Type</label>
                    <input
                        id="vehicle_type"
                        type="text"
                        placeholder="e.g. Van"
                        v-model="editingVehicle.type"
                        class="form-control"
                        :class="{ 'is-invalid': errors.type }"
                    >
                    <small
                        class="form-error"
                        v-if="errors.type"
                    >{{ errors.type[0] }}</small>
                </div>
                <div class="text-right">
                    <button
                        type="button"
                        class="btn btn-outline-secondary"
                        @click="closeVehicleModal"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        class="btn btn-primary"
                        :disabled="saving"
                    >
                        <span v-if="saving">Saving... <i class="fa fa-spinner fa-spin" /></span>
                        <span v-else>Save</span>
                    </button>
                </div>
            </form>
        </modal>

        <b-modal
            ref="deleteModal"
            title="Delete Vehicle"
            @ok="confirmDelete"
        >
            <p v-if="vehicleToDelete">
                Are you sure you want to delete vehicle {{ vehicleToDelete.registration }}?
            </p>
        </b-modal>
    </div>
</template>

<script>
import Modal from '../../../modals/ModalComponent.vue';
import VehicleService from '../../../../services/VehicleService';

export default {
    components: {
        Modal,
    },

    data() {
        return {
            vehicles: [],
            loading: false,
            saving: false,
            editingVehicle: null,
            vehicleToDelete: null,
            errors: {},
        };
    },

    computed: {
        modalTitle() {
            if (!this.editingVehicle || !this.editingVehicle.id) {
                return 'New Vehicle';
            }
            return 'Edit Vehicle';
        },
    },

    mounted() {
        this.loadVehicles();
    },

    methods: {
        loadVehicles() {
            this.loading = true;
            VehicleService.index().then((res) => {
                this.vehicles = res.data;
            }).catch(() => {
                alert('Error: Failed to load vehicles');
            }).then(() => {
                this.loading = false;
            });
        },

        openCreateModal() {
            this.editingVehicle = {
                registration: '',
                type: '',
            };
            this.errors = {};
            this.$refs.vehicleModal.open();
        },

        openEditModal(vehicle) {
            this.editingVehicle = {
                id: vehicle.id,
                registration: vehicle.registration,
                type: vehicle.type,
            };
            this.errors = {};
            this.$refs.vehicleModal.open();
        },

        onVehicleModalClose() {
            this.editingVehicle = null;
            this.errors = {};
        },

        closeVehicleModal() {
            this.onVehicleModalClose();
            this.$refs.vehicleModal.close();
        },

        saveVehicle() {
            if (this.saving) {
                return;
            }
            this.saving = true;
            this.errors = {};
            VehicleService.save(this.editingVehicle).then((res) => {
                const savedVehicle = res.data;
                const index = this.vehicles.findIndex((vehicle) => vehicle.id === savedVehicle.id);
                if (index === -1) {
                    this.vehicles.push(savedVehicle);
                } else {
                    this.vehicles.splice(index, 1, savedVehicle);
                }
                this.vehicles.sort((a, b) => {
                    if (a.registration < b.registration) {
                        return -1;
                    }
                    if (a.registration > b.registration) {
                        return 1;
                    }
                    return 0;
                });
                this.closeVehicleModal();
            }).catch((err) => {
                if (err.response && err.response.data && err.response.data.errors) {
                    this.errors = err.response.data.errors;
                    return;
                }
                alert('Error: Failed to save vehicle');
            }).then(() => {
                this.saving = false;
            });
        },

        promptDelete(vehicle) {
            this.vehicleToDelete = vehicle;
            this.$refs.deleteModal.show();
        },

        confirmDelete(bvModalEvt) {
            if (!this.vehicleToDelete) {
                return;
            }
            bvModalEvt.preventDefault();
            VehicleService.delete(this.vehicleToDelete.id).then(() => {
                const index = this.vehicles.findIndex((vehicle) => vehicle.id === this.vehicleToDelete.id);
                if (index !== -1) {
                    this.vehicles.splice(index, 1);
                }
                this.vehicleToDelete = null;
                this.$refs.deleteModal.hide();
            }).catch(() => {
                alert('Error: Failed to delete vehicle');
            });
        },
    },
};
</script>



// WEBPACK FOOTER //
// resources/assets/js/components/office/admin/vehicles/IndexComponent.vue